import "./MoveBookingPopup.scss";
import MidPopup from "../../../hoc/MidPopup/MidPopup";
import { IBooking, ICourt, ICourtType, ISummaryItem } from "../../../types/api";
import { useEffect, useState } from "react";
import {
  AdminCheckInBooking,
  moveBooking,
} from "../../../api/bookings/bookings";
import { toast } from "react-toastify";
import { capitalizeWords, formatDateToSwedish } from "../../../shared/utility";
import { BookingEnum, CourtTypeEnum, StatusEnum } from "../../../shared/enums";
import { getSlotByMember } from "../../../api/summaries/summaries";

interface IMoveBookingPopupProps {
  showPopup: boolean;
  onClose: () => void;
  onConfirm: () => void;
  bookedSlot?: ISummaryItem;
  slotForMovement?: ISummaryItem;
  newCourt?: ICourt;
  courts: ICourt[];
  filterDate: Date;
  currentCourtType: ICourtType;
}

function MoveBookingPopup(props: IMoveBookingPopupProps) {
  const {
    onClose,
    onConfirm,
    showPopup,
    bookedSlot,
    slotForMovement,
    newCourt,
    courts,
    filterDate,
    currentCourtType,
  } = props;
  const [isMoving, setIsMoving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const handleClose = () => {
    setWarningMessage(null);
    setSendEmail(true);
    onClose();
  };

  const fetchSlotByMember = async () => {
    if (
      slotForMovement === undefined ||
      bookedSlot === undefined ||
      newCourt === undefined
    ) {
      return;
    }
    setIsLoading(true);
    const dateTimeString = `${filterDate.toISOString().split("T")[0]}T${
      slotForMovement.start_time
    }`;
    try {
      const response = await getSlotByMember(
        dateTimeString,
        newCourt.court_id,
        currentCourtType,
        bookedSlot.booking[0].member_id,
        false
      );
      if (response.available_for_member_type === false) {
        setWarningMessage("Ny tid ligger utanför huvudbokarens spelrätt");
      }

      
    } catch (error) {
      toast.error(`Failed to fetch slot by member: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (showPopup) {
      fetchSlotByMember();
    }
  }, [showPopup]);

  const handleMoveBooking = async () => {
    if (
      slotForMovement === undefined ||
      bookedSlot === undefined ||
      newCourt === undefined
    ) {
      return;
    }
    setIsMoving(true);
    const isMovingToast = toast.loading("Flyttar bokning...");
    const oldBooking = bookedSlot.booking[0];
    let newBooking = JSON.parse(
      JSON.stringify(bookedSlot.booking[0])
    ) as IBooking;

    if (newBooking) {
      newBooking.datetime_start =
        filterDate.toISOString().split("T")[0] +
        "T" +
        slotForMovement.start_time;
      newBooking.court_id = newCourt.court_id;
    }
    if (oldBooking.booking_type === BookingEnum.DROP_IN) {
      newBooking.booking_type = BookingEnum.DROP_IN;
    }
    try {
      const response = await moveBooking(
        newBooking,
        oldBooking.datetime_start,
        oldBooking.court_id,
        sendEmail
      );

      if (response && !("message" in response)) {
        try {
          // Give all guests and co-players their old status
          if (response && response.booking_id) {
            try {
              if (
                newBooking.member_status !== StatusEnum.CONFIRMED &&
                newBooking.member_status !== StatusEnum.PENDING
              ) {
                const response = await AdminCheckInBooking(
                  newBooking.booking_id,
                  newBooking.datetime_start,
                  newBooking.member_id,
                  "member",
                  oldBooking.member_status,
                  false
                );
              }
            } catch (error: any) {
              toast.error(`Failed to update main booker status: ${error}`);
            }
            // Handle co-players
            for (const coPlayer of oldBooking.co_players) {
              if (
                coPlayer.status === StatusEnum.CONFIRMED ||
                coPlayer.status === StatusEnum.PENDING
              ) {
                continue;
              }
              try {
                await AdminCheckInBooking(
                  newBooking.booking_id,
                  newBooking.datetime_start,
                  coPlayer.member_id,
                  "co_player",
                  coPlayer.status,
                  false
                );
              } catch (error: any) {
                toast.error(`Failed to update co-player status: ${error}`);
              }
            }

            // Handle guests
            for (const guest of oldBooking.guests) {
              if (guest.member_id === "guest") {
                continue;
              }
              let isFamilyGuest = false;
              if (
                oldBooking.final_price.some(
                  (price) =>
                    price.event.includes(guest.member_id) && price.price === 40
                )
              ) {
                isFamilyGuest = true;
              }
              try {
                await AdminCheckInBooking(
                  newBooking.booking_id,
                  newBooking.datetime_start,
                  guest.member_id,
                  "guest",
                  guest.status,
                  isFamilyGuest
                );
              } catch (error: any) {
                toast.error(`Failed to update guest status: ${error}`);
              }
            }
          }
        } catch (error: any) {
          toast.error(error);
        }

        setIsMoving(false);
        toast.dismiss(isMovingToast);
        toast.success("Bokningen har flyttats");
        onConfirm();
        onClose();
      } else {
        toast.dismiss(isMovingToast);
        setIsMoving(false);
      }
    } catch (error: any) {
      toast.dismiss(isMovingToast);
      setIsMoving(false);
      toast.error(error);
    }
  };
  if (
    !props.bookedSlot ||
    !props.slotForMovement ||
    bookedSlot === undefined ||
    slotForMovement === undefined
  ) {
    return null;
  }
  const bookedCourt = courts.find(
    (court) => court.court_id === bookedSlot.booking[0].court_id
  );
  const booking = bookedSlot.booking[0];
  const { dateAndMonth } = formatDateToSwedish(filterDate);

  const handleCheckPreMoveCheck = () => {
    //implement functionality to check if main booker has access to the new time slot
  };
  return (
    <MidPopup
      showPopup={showPopup}
      onClose={handleClose}
      onConfirm={onConfirm}
      leftTopElement={<span className="text-m">Flytta bokning</span>}
      isLoading={isMoving}
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form less-gap"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="general-popup-form-top">
            <button
              className="text-m"
              onClick={() => handleMoveBooking()}
              disabled={isMoving || isLoading}
            >
              Flytta
            </button>
          </div>
          <div className="general-popup-form-group mb-20">
            <div className="general-popup-form-field">
              {warningMessage && (
                <p className="text-danger text-m">{warningMessage}</p>
              )}
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Bokning: </span>
                <div className="text-m primary-text text-svg-wrapper">
                  {capitalizeWords(booking.member_name)} - {dateAndMonth}
                </div>
              </div>
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Medspelare: </span>
                <div className="text-m primary-text text-svg-wrapper">
                  {booking.co_players.length}
                </div>
              </div>
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Gäster: </span>
                <div className="text-m primary-text text-svg-wrapper">
                  {booking.guests.length}
                </div>
              </div>
              {booking.coach_id.length > 0 && (
                <div className="general-popup-flex-between">
                  <span className="text-m primary-text">Tränare: </span>
                  <div className="text-m primary-text text-svg-wrapper">
                    {booking.coach_id[0]}
                  </div>
                </div>
              )}
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Från: </span>
                <div className="text-m primary-text text-svg-wrapper">
                  Kl. {bookedSlot.start_time.slice(0, 5)} - Bana{" "}
                  {bookedCourt?.name}
                </div>
              </div>
              <div className="general-popup-flex-between">
                <span className="text-m primary-text ">Till:</span>
                <div className="text-m primary-text text-svg-wrapper">
                  Kl. {slotForMovement.start_time.slice(0, 5)} - Bana{" "}
                  {newCourt?.name}
                </div>
              </div>
              <div className="general-popup-flex-between">
                <span className="text-m secondary-text">
                  Skicka Email vid flytt
                </span>
                <label className="toggle text-m">
                  <input
                    type="checkbox"
                    name="closed"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(!sendEmail)}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>
        </form>
      </div>
    </MidPopup>
  );
}

export default MoveBookingPopup;
